import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, Building2, RefreshCw, Search, ShieldCheck } from "lucide-react";
import { getApiUrl } from "../../config/api";

interface SchoolRow {
  id: number;
  name: string;
  status?: string;
  maintenance_mode?: number | boolean;
  maintenance_message?: string | null;
}

const MaintenanceControls: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [globalMessage, setGlobalMessage] = useState("");
  const [schools, setSchools] = useState<SchoolRow[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<number | "">("");
  const [schoolEnabled, setSchoolEnabled] = useState(false);
  const [schoolMessage, setSchoolMessage] = useState("");
  const [globalSaving, setGlobalSaving] = useState(false);
  const [schoolSaving, setSchoolSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadData = async () => {
    const [globalRes, schoolsRes] = await Promise.all([
      axios.get(getApiUrl("superadmin/maintenance/status"), { headers }),
      axios.get(getApiUrl("superadmin/schools"), { headers })
    ]);
    setGlobalEnabled(!!globalRes.data?.enabled);
    setGlobalMessage(globalRes.data?.message || "");
    setSchools(schoolsRes.data || []);
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredSchools = useMemo(
    () => schools.filter((school) => school.name.toLowerCase().includes(search.toLowerCase())),
    [schools, search]
  );

  const selectedSchoolRecord = schools.find((school) => school.id === selectedSchool) || null;

  const updateGlobal = async () => {
    try {
      setGlobalSaving(true);
      setNotice("");
      await axios.put(getApiUrl("superadmin/maintenance/global"), { enabled: globalEnabled, message: globalMessage }, { headers });
      setNotice("Global maintenance settings saved.");
      await loadData();
    } catch (error: any) {
      setNotice(error?.response?.data?.message || "Failed to save global maintenance settings.");
    } finally {
      setGlobalSaving(false);
    }
  };

  const updateSchool = async () => {
    if (!selectedSchool) return;
    try {
      setSchoolSaving(true);
      setNotice("");
      await axios.put(getApiUrl(`superadmin/maintenance/schools/${selectedSchool}`), { enabled: schoolEnabled, message: schoolMessage }, { headers });
      setNotice("School maintenance settings saved.");
      await loadData();
    } catch (error: any) {
      setNotice(error?.response?.data?.message || "Failed to save school maintenance settings.");
    } finally {
      setSchoolSaving(false);
    }
  };

  const maintenanceCount = schools.filter((school) => school.maintenance_mode).length;

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
      <div className="mx-auto max-w-[1450px] space-y-6">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-slate-400">Maintenance Controls</p>
              <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-950">Platform and school maintenance windows</h1>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">
                Control global maintenance mode, override individual schools, and verify which tenants are currently restricted.
              </p>
            </div>
            <button onClick={loadData} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </section>

        <section className="grid gap-5 md:grid-cols-3">
          {[
            { label: "Global Status", value: globalEnabled ? "Enabled" : "Offline", Icon: AlertTriangle },
            { label: "Schools in Maintenance", value: String(maintenanceCount), Icon: Building2 },
            { label: "Schools Operating", value: String(Math.max(schools.length - maintenanceCount, 0)), Icon: ShieldCheck }
          ].map(({ label, value, Icon }) => (
            <article key={label} className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">{label}</p>
                  <p className="mt-3 text-3xl font-black text-slate-900">{value}</p>
                </div>
                <div className="rounded-2xl bg-slate-100 p-3 text-slate-700"><Icon size={20} /></div>
              </div>
            </article>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[1fr_1fr]">
          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-black text-slate-900">Global maintenance</h2>
            <p className="mt-2 text-sm text-slate-500">Use this only when the entire platform should be unavailable to tenant users.</p>
            <label className="mt-5 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-700">
              <input type="checkbox" checked={globalEnabled} onChange={(e) => setGlobalEnabled(e.target.checked)} />
              Enable platform-wide maintenance mode
            </label>
            <textarea
              className="mt-4 min-h-[140px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-slate-300 focus:bg-white"
              placeholder="Message shown to all tenant users during maintenance."
              value={globalMessage}
              onChange={(e) => setGlobalMessage(e.target.value)}
            />
            <button onClick={updateGlobal} disabled={globalSaving} className="mt-4 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white disabled:opacity-60">
              {globalSaving ? "Saving..." : "Save Global Settings"}
            </button>
          </article>

          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-black text-slate-900">Per-school override</h2>
            <p className="mt-2 text-sm text-slate-500">Restrict a specific tenant without affecting the rest of the platform.</p>
            <div className="relative mt-5">
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search school by name"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-12 py-3 text-sm outline-none transition focus:border-slate-300 focus:bg-white"
              />
            </div>
            <select
              className="mt-4 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-slate-300 focus:bg-white"
              value={selectedSchool}
              onChange={(e) => {
                const next = e.target.value ? Number(e.target.value) : "";
                setSelectedSchool(next);
                const school = schools.find((item) => item.id === next);
                setSchoolEnabled(!!school?.maintenance_mode);
                setSchoolMessage(school?.maintenance_message || "");
              }}
            >
              <option value="">Select school...</option>
              {filteredSchools.map((school) => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
            <label className="mt-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-700">
              <input type="checkbox" checked={schoolEnabled} onChange={(e) => setSchoolEnabled(e.target.checked)} disabled={!selectedSchool} />
              Enable maintenance for selected school
            </label>
            <textarea
              className="mt-4 min-h-[120px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-slate-300 focus:bg-white"
              placeholder="Message shown to the selected school's users."
              value={schoolMessage}
              onChange={(e) => setSchoolMessage(e.target.value)}
              disabled={!selectedSchool}
            />
            {selectedSchoolRecord && (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                <div className="font-semibold text-slate-800">{selectedSchoolRecord.name}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">
                  Status: {selectedSchoolRecord.status || "unknown"} · Maintenance {selectedSchoolRecord.maintenance_mode ? "enabled" : "disabled"}
                </div>
              </div>
            )}
            <button onClick={updateSchool} disabled={!selectedSchool || schoolSaving} className="mt-4 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white disabled:opacity-60">
              {schoolSaving ? "Saving..." : "Save School Settings"}
            </button>
          </article>
        </section>

        {notice && <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">{notice}</div>}
      </div>
    </div>
  );
};

export default MaintenanceControls;
